import { useRef, useState } from "react";
import Button, { ButtonType } from "../../components/Button/Button";
import Icon from "../../components/Icon/icon";

interface IUploadButtonProps {
  onUploadFileSubmit: (csvFileArray: string[][]) => void;
  isLoading: boolean;
}

const UploadButton = ({ onUploadFileSubmit, isLoading }: IUploadButtonProps) => {
  const [csvFileArray, setCsvFileArray] = useState<string[][]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isValidLine = (line: string[]) => {
    const containsOnlyNumbers = (str: string) => /^\d+$/.test(str);
    if (line.length < 2) return false;
    if (line[0] === "" || !containsOnlyNumbers(line[0])) return false;
    if (line[1] === "" || !containsOnlyNumbers(line[1])) return false;
    return true;
  };

  const parseCSV = (text: string) => {
    const lines = text.split("\n");
    lines.shift();
    const linesFormatted = lines.map((line) => line.split(","));
    const linesValid = linesFormatted.filter(isValidLine);
    setCsvFileArray([...linesValid]);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event) return;
    const uploadedCSV =
      event.target.files && event.target.files.length > 0 && event.target.files[0];
    if (uploadedCSV) {
      setFileName(uploadedCSV.name);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          const result = evt?.target?.result;
          if (typeof result === "string") resolve(parseCSV(result));
        };
        reader.onerror = reject;
        reader.readAsText(uploadedCSV);
      });
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file && file.name.endsWith(".csv")) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (evt) => {
        const result = evt?.target?.result;
        if (typeof result === "string") parseCSV(result);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="util">
      <div className="inputs">
        <input
          ref={inputRef}
          type="file"
          id="file-upload"
          accept=".csv"
          onChange={handleFileUpload}
          className="file-input-hidden"
        />
        <label
          htmlFor="file-upload"
          className={`file-drop-zone${isDragOver ? " file-drop-zone--drag-over" : ""}${fileName ? " file-drop-zone--selected" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          <span className="file-drop-zone__icon">
            <Icon name="FileUpload" />
          </span>
          <span className="file-drop-zone__text">
            {fileName ? fileName : "Choose CSV file or drag & drop"}
          </span>
          {csvFileArray.length > 0 && (
            <span className="file-drop-zone__count">{csvFileArray.length} items found</span>
          )}
        </label>
      </div>

      <Button
        type={ButtonType.primary}
        onClick={() => onUploadFileSubmit(csvFileArray)}
        disabled={isLoading || csvFileArray.length === 0}
        style={{ minWidth: "100px", padding: "0.5rem 1rem" }}
      >
        Import
      </Button>
    </div>
  );
};

export default UploadButton;
